import React, { useState } from 'react';
import { XIcon, CheckIcon, AlertCircle, Zap } from 'lucide-react';
import { Button } from '../Button';
type RechargePlan = {
  id: string;
  data: string;
  validity: string;
  price: number;
  discount?: number;
  speed?: string;
  features?: string[];
  recommended?: boolean;
  promo?: string;
};
interface RechargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  esimDetails?: {
    id: string;
    number: string;
    country: string;
    flag: string;
    dataLeft: string;
    validUntil: string;
  };
}
type Step = 'select-plan' | 'confirm' | 'payment' | 'success';
export function RechargeModal({
  isOpen,
  onClose,
  esimDetails
}: RechargeModalProps) {
  const [step, setStep] = useState<Step>('select-plan');
  const [selectedPlan, setSelectedPlan] = useState<RechargePlan | null>(null);
  const rechargePlans: RechargePlan[] = [{
    id: 'quick',
    data: '2GB',
    validity: '3 days',
    price: 4.99,
    speed: '4G/5G',
    features: ['Emergency top-up', 'Instant activation'],
    promo: 'Perfect for short trips'
  }, {
    id: 'basic',
    data: '5GB',
    validity: '7 days',
    price: 9.99,
    speed: '4G/5G',
    discount: 10,
    features: ['Rollover data', 'Social media bonus'],
    recommended: true,
    promo: 'Most Popular'
  }, {
    id: 'standard',
    data: '10GB',
    validity: '15 days',
    price: 18.99,
    speed: '4G/5G',
    discount: 15,
    features: ['Rollover data', 'Video streaming optimization', 'Priority support'],
    promo: 'Best Value'
  }, {
    id: 'premium',
    data: '20GB',
    validity: '30 days',
    price: 29.99,
    speed: '4G/5G',
    discount: 25,
    features: ['Unlimited social media', 'HD video streaming', '24/7 Priority support', 'Data sharing'],
    promo: 'Maximum Flexibility'
  }];
  if (!isOpen) return null;
  const handlePlanSelect = (plan: RechargePlan) => {
    setSelectedPlan(plan);
    setStep('confirm');
  };
  const handleConfirm = () => {
    setStep('payment');
  };
  const handlePayment = () => {
    setStep('success');
  };
  const getUsageRecommendation = (data: string) => {
    const gb = parseInt(data);
    if (gb <= 2) return 'Ideal for light browsing and messaging';
    if (gb <= 5) return 'Perfect for social media and occasional streaming';
    if (gb <= 10) return 'Great for regular video calls and streaming';
    return 'Best for heavy data users and HD streaming';
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" data-id="element-213">
      <div className="bg-white rounded-2xl w-full max-w-2xl mx-4 relative overflow-hidden" data-id="element-214">
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-center" data-id="element-215">
          <h2 className="text-xl font-bold" data-id="element-216">
            {step === 'select-plan' && 'Recharge eSIM'}
            {step === 'confirm' && 'Confirm Selection'}
            {step === 'payment' && 'Payment'}
            {step === 'success' && 'Success'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-217">
            <XIcon className="w-6 h-6" data-id="element-218" />
          </button>
        </div>
        {/* Content */}
        <div className="p-6" data-id="element-219">
          {/* eSIM Details */}
          {esimDetails && <div className="bg-blue-50 rounded-xl p-4 mb-6" data-id="element-220">
              <div className="flex items-center justify-between" data-id="element-221">
                <div className="flex items-center" data-id="element-222">
                  <span className="text-2xl mr-3" data-id="element-223">{esimDetails.flag}</span>
                  <div data-id="element-224">
                    <h3 className="font-semibold" data-id="element-225">{esimDetails.country}</h3>
                    <p className="text-sm text-gray-600" data-id="element-226">
                      eSIM: {esimDetails.number}
                    </p>
                  </div>
                </div>
                <div data-id="element-227">
                  <p className="text-sm text-gray-600" data-id="element-228">
                    Current Data: {esimDetails.dataLeft}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-229">
                    Valid until: {esimDetails.validUntil}
                  </p>
                </div>
              </div>
            </div>}
          {step === 'select-plan' && <div className="space-y-4" data-id="element-230">
              {/* Data Usage Guide */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6" data-id="element-231">
                <h3 className="font-semibold mb-2" data-id="element-232">
                  Not sure how much data you need?
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-233">
                  <div className="flex items-center gap-2" data-id="element-234">
                    <span className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-235"></span>
                    <span data-id="element-236">1 hour video call ≈ 500MB</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-237">
                    <span className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-238"></span>
                    <span data-id="element-239">1 hour HD streaming ≈ 2GB</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-240">
                    <span className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-241"></span>
                    <span data-id="element-242">Social media/day ≈ 250MB</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-243">
                    <span className="w-2 h-2 bg-blue-500 rounded-full" data-id="element-244"></span>
                    <span data-id="element-245">Web browsing/day ≈ 100MB</span>
                  </div>
                </div>
              </div>
              {/* Plans */}
              <div className="grid grid-cols-1 gap-4" data-id="element-246">
                {rechargePlans.map(plan => <button key={plan.id} onClick={() => handlePlanSelect(plan)} className={`w-full p-6 border-2 rounded-xl hover:border-blue-500 transition-colors relative overflow-hidden ${plan.recommended ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`} data-id="element-247">
                    {/* Promo Tag */}
                    {plan.promo && <div className="absolute top-0 right-0 bg-blue-500 text-white px-3 py-1 text-sm rounded-bl-xl" data-id="element-248">
                        {plan.promo}
                      </div>}
                    <div className="flex justify-between items-start mb-4 pt-6" data-id="element-249">
                      <div data-id="element-250">
                        <h3 className="text-xl font-semibold" data-id="element-251">{plan.data}</h3>
                        <p className="text-gray-600" data-id="element-252">
                          Valid for {plan.validity}
                        </p>
                        <p className="text-sm text-gray-500 mt-1" data-id="element-253">
                          {getUsageRecommendation(plan.data)}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-254">
                        {plan.discount > 0 && <div className="text-sm text-red-500 mb-1" data-id="element-255">
                            Save {plan.discount}%
                          </div>}
                        <div className="flex items-center gap-2 justify-end" data-id="element-256">
                          {plan.discount > 0 && <span className="text-sm text-gray-500 line-through" data-id="element-257">
                              $
                              {(plan.price / (1 - plan.discount / 100)).toFixed(2)}
                            </span>}
                          <span className="text-2xl font-bold" data-id="element-258">
                            ${plan.price}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Features */}
                    <div className="space-y-2 mt-4 text-sm text-gray-600" data-id="element-259">
                      <div className="flex items-center gap-2" data-id="element-260">
                        <Zap className="h-4 w-4 text-blue-500" data-id="element-261" />
                        <span data-id="element-262">Speed: {plan.speed}</span>
                      </div>
                      {plan.features?.map((feature, index) => <div key={index} className="flex items-center gap-2" data-id="element-263">
                          <CheckIcon className="h-4 w-4 text-green-500" data-id="element-264" />
                          <span data-id="element-265">{feature}</span>
                        </div>)}
                    </div>
                  </button>)}
              </div>
            </div>}
          {step === 'confirm' && selectedPlan && <div className="space-y-6" data-id="element-266">
              <div className="border border-gray-200 rounded-xl p-6" data-id="element-267">
                <h3 className="font-semibold mb-4" data-id="element-268">Selected Plan</h3>
                <div className="space-y-3" data-id="element-269">
                  <div className="flex justify-between" data-id="element-270">
                    <span data-id="element-271">Data</span>
                    <span className="font-semibold" data-id="element-272">{selectedPlan.data}</span>
                  </div>
                  <div className="flex justify-between" data-id="element-273">
                    <span data-id="element-274">Validity</span>
                    <span className="font-semibold" data-id="element-275">
                      {selectedPlan.validity}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-276">
                    <span data-id="element-277">Price</span>
                    <span className="font-semibold" data-id="element-278">${selectedPlan.price}</span>
                  </div>
                </div>
              </div>
              <Button variant="primary" fullWidth onClick={handleConfirm} data-id="element-279">
                Confirm Selection
              </Button>
              <Button variant="outline" fullWidth onClick={() => setStep('select-plan')} data-id="element-280">
                Change Plan
              </Button>
            </div>}
          {step === 'payment' && <div className="space-y-6" data-id="element-281">
              <div className="space-y-4" data-id="element-282">
                <div data-id="element-283">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-284">
                    Card Number
                  </label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-xl" placeholder="1234 5678 9012 3456" data-id="element-285" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-286">
                  <div data-id="element-287">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-288">
                      Expiry Date
                    </label>
                    <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-xl" placeholder="MM/YY" data-id="element-289" />
                  </div>
                  <div data-id="element-290">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-291">
                      CVC
                    </label>
                    <input type="text" className="w-full px-4 py-3 border border-gray-200 rounded-xl" placeholder="123" data-id="element-292" />
                  </div>
                </div>
              </div>
              <Button variant="primary" fullWidth onClick={handlePayment} data-id="element-293">
                Pay ${selectedPlan?.price}
              </Button>
            </div>}
          {step === 'success' && <div className="text-center" data-id="element-294">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-295">
                <CheckIcon className="w-8 h-8 text-green-500" data-id="element-296" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-297">
                Recharge Successful!
              </h3>
              <p className="text-gray-600 mb-6" data-id="element-298">
                Your eSIM has been recharged and the data will be added
                immediately.
              </p>
              <div className="bg-blue-50 rounded-xl p-6 mb-6 text-left" data-id="element-299">
                <div className="flex items-center mb-2" data-id="element-300">
                  <AlertCircle className="h-5 w-5 text-blue-500 mr-2" data-id="element-301" />
                  <span className="font-semibold" data-id="element-302">Important</span>
                </div>
                <p className="text-sm text-gray-600" data-id="element-303">
                  If you don't see the additional data immediately, please wait
                  a few minutes and restart your device if necessary.
                </p>
              </div>
              <Button variant="primary" fullWidth onClick={onClose} data-id="element-304">
                Done
              </Button>
            </div>}
        </div>
      </div>
    </div>;
}